/**
 * GlobalJobsBanner — shows above the main content whenever there is at least
 * one active enrich/scrape job. Multiple concurrent jobs are stacked.
 *
 * Persists across navigation because the underlying state lives in
 * JobsContext (mounted at app root).
 *
 * For enrich_all jobs, EnrichAllLiveBanner is the SINGLE banner regardless
 * of state (running / paused). It exposes Pause/Stop when running and
 * Resume/Trash when paused — resume/delete used to live in a separate
 * yellow chip but that was redundant and confusing (see git history).
 */
import React, { useState } from "react";
import { Loader2, X, CheckCircle2, XCircle } from "lucide-react";
import { useJobs, defaultLabel } from "@/contexts/JobsContext";
import { Button } from "@/components/ui/button";
import EnrichAllLiveBanner from "@/components/EnrichAllLiveBanner";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function shortProgress(job) {
  const s = job.summary || {};
  if (job.type === "enrich_all") {
    return `${s.processed ?? 0} / ${s.targets ?? 0} · ${s.successful ?? 0} ok · ${s.failed ?? 0} failed`;
  }
  if (job.type === "photo_check") {
    return `${s.processed ?? 0} / ${s.targets ?? 0} checked`;
  }
  return "";
}

/**
 * Short human summary shown on the "completed" banner strip so a returning
 * admin can see AT A GLANCE what a background job accomplished. Keep it
 * one line — details live in the ScrapeHistory page.
 */
function finishedSummary(job) {
  const s = job.summary || {};
  if (job.type === "photo_check") {
    return `${s.checked ?? 0} checked · ${s.faces ?? 0} faces · ${s.logos ?? 0} logos · ${s.placeholders ?? 0} placeholders`;
  }
  if (job.type === "enrich_all") {
    return `${s.successful ?? 0} succeeded · ${s.failed ?? 0} failed · ${s.credits_used_in_run ?? 0} Firecrawl credits used`;
  }
  if (job.type === "enrich_realtor") {
    return `Filled ${s.fields_filled ?? 0} field${s.fields_filled === 1 ? "" : "s"}`;
  }
  if (job.type === "scrape_brokerage") {
    return `${s.realtors_found ?? 0} realtors · ${s.new_added ?? 0} new`;
  }
  if (job.type === "scrape_all") {
    return `${s.successful ?? 0} ok · ${s.failed ?? 0} failed`;
  }
  if (job.type === "enrich_personal_websites") {
    return `${s.succeeded ?? 0} sites · ${s.fields_filled ?? 0} fields filled`;
  }
  return "";
}

export default function GlobalJobsBanner() {
  const { activeJobs, recentlyFinishedJobs, dismiss, track } = useJobs();
  const [showAll, setShowAll] = useState(false);

  const doPauseActive = async (jobId) => {
    try {
      await api.post(`/realtors/enrich-all/${jobId}/pause`);
      toast.message("Pause requested", {
        description:
          "Finishing the current realtor first, then the job will pause. Resume from this banner when you're ready.",
      });
    } catch (e) {
      toast.error("Couldn't pause enrichment", { description: formatApiError(e) });
    }
  };

  const doStopActive = async (jobId) => {
    if (typeof window !== "undefined" &&
        !window.confirm(
          "Stop this enrichment entirely? The queue will be discarded (no resume). Any realtors already searched keep their new data.",
        )) {
      return;
    }
    try {
      await api.post(`/realtors/enrich-all/${jobId}/stop`);
      toast.message("Stop requested", {
        description:
          "Finishing the current realtor, then the job will end. The remaining queue is being discarded.",
      });
    } catch (e) {
      toast.error("Couldn't stop enrichment", { description: formatApiError(e) });
    }
  };

  const doResume = async (jobId) => {
    try {
      const { data } = await api.post(`/realtors/enrich-all/${jobId}/resume`);
      if (data?.status === "running") {
        toast.success(`Resuming ${data.resumed_targets ?? 0} realtors`, {
          description: "The banner will switch back to live progress in a moment.",
        });
        // Re-track so the banner picks up the fresh running state.
        track(jobId, { type: "enrich_all", status: "running" });
      } else if (data?.status === "already_done") {
        toast.message("Nothing left to resume", {
          description: "All queued realtors have already been processed or removed.",
        });
        dismiss(jobId);
      }
    } catch (e) {
      toast.error("Couldn't resume enrichment", { description: formatApiError(e) });
    }
  };

  const doDeletePaused = async (jobId) => {
    if (typeof window !== "undefined" &&
        !window.confirm(
          "Delete this paused enrichment? The queued realtors won't be searched (you can always run a new enrich pass on them later).",
        )) {
      return;
    }
    try {
      await api.delete(`/realtors/enrich-all/${jobId}`);
      toast.success("Paused enrichment discarded", {
        description: "Run a new enrich pass anytime.",
      });
      dismiss(jobId);
    } catch (e) {
      toast.error("Couldn't delete enrichment", { description: formatApiError(e) });
    }
  };

  if (!activeJobs.length && !recentlyFinishedJobs.length) return null;
  const MAX_VISIBLE = 3;
  const visible = showAll ? activeJobs : activeJobs.slice(0, MAX_VISIBLE);
  const hiddenCount = activeJobs.length - visible.length;

  return (
    <div className="space-y-2" data-testid="global-jobs-banner">
      {visible.map((job) => {
        // Bulk enrich jobs render a rich live-parade banner (avatar +
        // per-platform icons that pop in as socials are discovered).
        // Handles running AND paused states in-banner.
        if (job.type === "enrich_all") {
          return (
            <EnrichAllLiveBanner
              key={job.id}
              job={job}
              onPause={doPauseActive}
              onStop={doStopActive}
              onResume={doResume}
              onDelete={doDeletePaused}
            />
          );
        }
        const progress = shortProgress(job);
        return (
          <div
            key={job.id}
            className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5 flex items-center gap-3"
            data-testid={`global-job-${job.id}`}
            role="status"
          >
            <Loader2 className="h-5 w-5 animate-spin text-blue-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-blue-900 truncate">
                {defaultLabel(job)}
                {job.target_label ? (
                  <span className="text-blue-700 font-normal"> · {job.target_label}</span>
                ) : null}
              </div>
              {progress ? (
                <div className="text-xs text-blue-700 mt-0.5 truncate">{progress}</div>
              ) : (
                <div className="text-xs text-blue-700 mt-0.5">
                  Running in background. You can keep working — we&apos;ll notify you when it finishes.
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Hide banner"
              className="h-7 w-7 text-blue-700 hover:bg-blue-100 shrink-0"
              onClick={() => dismiss(job.id)}
              data-testid={`global-job-dismiss-${job.id}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
      {hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="w-full text-xs text-blue-700 hover:text-blue-900 font-medium px-3 py-1.5 border border-dashed border-blue-200 rounded-md transition-colors"
          data-testid="global-jobs-show-all"
        >
          + {hiddenCount} more background {hiddenCount === 1 ? "job" : "jobs"} · show all
        </button>
      ) : showAll && activeJobs.length > MAX_VISIBLE ? (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="w-full text-xs text-blue-700 hover:text-blue-900 font-medium px-3 py-1.5 border border-dashed border-blue-200 rounded-md transition-colors"
          data-testid="global-jobs-collapse"
        >
          Collapse
        </button>
      ) : null}
      {/* Recently-completed jobs — kept visible for 5 minutes (or until X'd)
          so a returning admin can actually SEE what finished while they
          were on another tab. Previously the banner disappeared the moment
          the job went 'done' and the only completion signal was a 5-second
          toast — which was easy to miss. */}
      {recentlyFinishedJobs.map((job) => {
        const ok = job.status === "done";
        const errs = (job.errors || []).filter(Boolean);
        return (
          <div
            key={`done-${job.id}`}
            className={`rounded-md border px-4 py-2.5 flex items-center gap-3 ${
              ok
                ? "border-emerald-200 bg-emerald-50"
                : "border-rose-200 bg-rose-50"
            }`}
            data-testid={`global-job-finished-${job.id}`}
            role="status"
          >
            {ok ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-rose-600 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium truncate ${ok ? "text-emerald-900" : "text-rose-900"}`}>
                {defaultLabel(job)} — {ok ? "complete" : "failed"}
                {job.target_label ? (
                  <span className={`font-normal ${ok ? "text-emerald-700" : "text-rose-700"}`}>
                    {" "}· {job.target_label}
                  </span>
                ) : null}
              </div>
              <div className={`text-xs mt-0.5 truncate ${ok ? "text-emerald-800" : "text-rose-800"}`}>
                {ok ? finishedSummary(job) || "Finished" : (errs[0] || "Job failed").slice(0, 180)}
                {job.finished_at ? (
                  <span className={ok ? "text-emerald-600 ml-1" : "text-rose-600 ml-1"}>
                    {" · "}
                    {new Date(job.finished_at).toLocaleTimeString()}
                  </span>
                ) : null}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Dismiss"
              className={`h-7 w-7 shrink-0 ${
                ok
                  ? "text-emerald-700 hover:bg-emerald-100"
                  : "text-rose-700 hover:bg-rose-100"
              }`}
              onClick={() => dismiss(job.id)}
              data-testid={`global-job-finished-dismiss-${job.id}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
